import { computed, Directive, effect, inject, input, signal } from '@angular/core';
import { FilterBean } from '@type/filter.type';
import { NamedId } from '@type/name-id.type';
import { FilterDictionariesService } from '../services/filter-dictionaries/filter-dictionaries.service';
import { BaseFilterItem } from './base-filter-item.directive';

@Directive()
export abstract class ReferenceFilter<T extends FilterBean> extends BaseFilterItem<T> {
  protected fds = inject(FilterDictionariesService);

  public opened = input<boolean>(false);

  public viewDictionary = computed(() =>
    this.dictionary().filter(({ name }) => name.toLowerCase().includes(this.query().trim().toLowerCase())),
  );

  public query = signal('');

  public selectedSet = new Set<any>();

  public get allChecked() {
    return (
      !!this.selectedSet.size &&
      this.count() - +this.showCheckboxForEmptyValue === this.selectedSet.size &&
      this.viewDictionary().every(({ id }) => this.selectedSet.has(id))
    );
  }

  public get isIndeterminate() {
    return !this.allChecked && !!this.selectedSet.size;
  }

  abstract count: () => number;

  abstract dictionary: () => NamedId[];

  abstract defaultFilter(): T;

  protected useEffect = () => {
    const dict = this.dictionary();
    const filter = this.filter.value;
    if (dict && dict.length && Array.isArray(filter)) {
      filter.forEach((id) => this.selectedSet.add(id));
      this.updateFilter();
    }
  };

  #e = effect(this.useEffect);

  toggle(val: { checked: boolean }) {
    if (val.checked) {
      this.filter.is_null = true;
      this.viewDictionary().forEach(({ id }) => this.selectedSet.add(id));
      this.cdr.markForCheck();
    } else {
      this.resetFilterItem();
    }

    this.updateFilterChangeTime();
  }

  toggleOne(id: number) {
    this.selectedSet.has(id) ? this.selectedSet.delete(id) : this.selectedSet.add(id);
    this.updateFilterChangeTime();
  }

  protected convertFilterToChips(): string[] {
    return [(this.filter.value?.length || 0) + +!!this.filter?.is_null].filter(Boolean).map((el) => el.toString());
  }

  protected resetFilterItem(): void {
    Array.isArray(this.filter.value) && (this.filter.value.length = 0);
    this.filter.is_null = false;
    this.selectedSet.clear();
    this.cdr.markForCheck();
  }

  protected getDefaultFilter(): T {
    return { ...this.defaultFilter(), value: [], name: this.filter?.name || '', model: this.filter?.model || '' };
  }

  protected processFilterForView(): void {
    this.filter.model = this.row().model;
    this.filter.dictionaryModel = this.row().dictionaryModel;
    this.filter.name = this.row().name;
  }

  protected updateFilter() {
    this.filter.value = Array.from(this.selectedSet.values());
  }

  protected getNecessaryProperties(filter: FilterBean) {
    return {
      is_null: filter.is_null,
      value: filter.value,
      model: filter.model,
      dictionaryModel: filter.dictionaryModel,
    };
  }
}
